import { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { fetchAll } from '../lib/fetchAll'
import { MAX_FILE_MB, checkFiles, uploadToStorage, registerAttachments, filesFromClipboard, sendEmail as postEmail } from '../lib/attachments'
import { Icon, Avatar, Spinner, formatSize, FileIcon } from './ui'

// Identificativo del ticket generato prima di salvarlo: serve per caricare gli allegati per primi
const newId = () => (window.crypto?.randomUUID
  ? window.crypto.randomUUID()
  : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => { const r = Math.random() * 16 | 0; return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16) }))

export default function NewTicket({ profile, onCreated }) {
  const [title, setTitle] = useState('')
  const [internalRef, setInternalRef] = useState('')
  const [description, setDescription] = useState('')
  const [assignedTo, setAssignedTo] = useState('')
  const [recipientEmail, setRecipientEmail] = useState('')
  const [profiles, setProfiles] = useState([])
  const [emailOptions, setEmailOptions] = useState([])
  const [pendingFiles, setPendingFiles] = useState([])
  const [showEmailList, setShowEmailList] = useState(false)
  const [drag, setDrag] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [created, setCreated] = useState(null)   // ticket gia' creato con un problema successivo: niente doppioni
  const [boxes, setBoxes] = useState({})           // caselle email e operatore collegato

  useEffect(() => {
    supabase.from('profiles').select('id, name').then(({ data }) => setProfiles(data || []))
    supabase.from('settings').select('key, value')
      .in('key', ['mailbox1_email', 'mailbox1_operator_id', 'mailbox2_email', 'mailbox2_operator_id'])
      .then(({ data }) => { const m = {}; (data || []).forEach(r => { m[r.key] = r.value }); setBoxes(m) })
    fetchAll(() => supabase.from('tickets').select('recipient_email').not('recipient_email', 'is', null).order('id')).then(({ data }) => {
      const uniq = [...new Set((data || []).map(r => r.recipient_email).filter(Boolean))].sort()
      setEmailOptions(uniq)
    })
  }, [])

  const addFiles = (files) => { if (files.length) { setError(''); setPendingFiles(prev => [...prev, ...files]) } }

  // Ctrl+V di uno screenshot nella descrizione
  const handlePaste = (e) => {
    const files = filesFromClipboard(e)
    if (files.length) { e.preventDefault(); addFiles(files) }
  }

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files)
    addFiles(files)
    e.target.value = ''
  }

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index))
  }

  const onDrop = (e) => {
    e.preventDefault()
    setDrag(false)
    const files = Array.from(e.dataTransfer?.files || [])
    if (files.length) addFiles(files)
  }

  // Ordine sicuro: 1) allegati nello storage  2) ticket  3) registrazione allegati  4) email.
  // Se un allegato non si carica non viene creato nulla e si puo' riprovare senza doppioni.
  const handleSubmit = async () => {
    if (created) return
    if (!title.trim()) { setError('L\'oggetto è obbligatorio.'); return }
    const problems = checkFiles(pendingFiles)
    if (problems.length) { setError(`Allegati non validi: ${problems.join(', ')}. Rimuovili e riprova.`); return }
    setLoading(true); setError('')
    try {
      const id = newId()
      const up = await uploadToStorage(id, pendingFiles)
      if (up.error) { setError(`Allegato non caricato — ${up.error}. Il ticket non è stato creato: riprova, oppure rimuovi quel file.`); return }

      const row = {
        id,
        title: title.trim(), description: description.trim() || null,
        created_by: profile.id,
        assigned_to: assignedTo || null, recipient_email: recipientEmail.trim() || null
      }
      // Il riferimento interno si manda solo se scritto: cosi' funziona anche prima di creare la colonna
      if (internalRef.trim()) row.internal_ref = internalRef.trim().slice(0, 100)
      let { data: ticket, error: insertError } = await supabase.from('tickets').insert(row).select().single()
      let refLost = false
      if (insertError && row.internal_ref && /internal_ref/.test(insertError.message || '')) {
        delete row.internal_ref; refLost = true
        ;({ data: ticket, error: insertError } = await supabase.from('tickets').insert(row).select().single())
      }
      if (insertError || !ticket) {
        if (up.uploaded.length) await supabase.storage.from('attachments').remove(up.uploaded.map(u => u.path)).catch(() => {})
        setError(`Errore durante la creazione del ticket: ${insertError?.message || 'riprova'}.`)
        return
      }

      const reg = await registerAttachments({ ticketId: ticket.id, commentId: null, uploaded: up.uploaded, userId: profile.id })
      const issues = []
      if (refLost) issues.push('il riferimento interno non è stato salvato (manca la colonna nel database)')
      if (reg.error) issues.push(`gli allegati non sono stati registrati (${reg.error})`)

      if (recipientEmail.trim()) {
        try {
          const out = await postEmail({
            type: 'new_ticket',
            ticket: { ...ticket, created_by_name: profile.name },
            mailbox: outgoingMailbox,
            attachments: reg.rows.map(a => ({ path: a.file_path, name: a.file_name }))
          })
          if (out.missingAttachments?.length) issues.push(`l'email è partita senza: ${out.missingAttachments.join(', ')}`)
        } catch (e) {
          issues.push(`l'email al cliente NON è partita (${e.message})`)
        }
      }

      if (issues.length) {
        setCreated(ticket)
        setError(`Ticket${ticket.number != null ? ` #${ticket.number}` : ''} creato, ma ${issues.join('; ')}.`)
        return
      }
      onCreated()
    } finally {
      setLoading(false)
    }
  }

  // Casella da cui parte l'email: quella dell'operatore assegnato, la stessa regola delle risposte
  // (cosi' il cliente riceve tutto dallo stesso indirizzo); senza operatore collegato, la casella principale
  const outgoingMailbox = !assignedTo ? undefined
    : boxes.mailbox1_operator_id === assignedTo && boxes.mailbox1_email ? boxes.mailbox1_email
    : boxes.mailbox2_operator_id === assignedTo && boxes.mailbox2_email ? boxes.mailbox2_email
    : undefined

  const filteredEmails = emailOptions.filter(e => e.toLowerCase().includes(recipientEmail.trim().toLowerCase()))
  const hasEmail = !!recipientEmail.trim()

  return (
    <div>
      <div className="crumbs" style={{ marginBottom: 12 }}>
        <button onClick={onCreated}><Icon name="arrowLeft" size={15} /> Ticket</button>
        <span className="sep">/</span>
        <span>Nuovo</span>
      </div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Nuovo ticket</h1>
          <p className="page-sub">Registra una richiesta di assistenza e, se vuoi, avvisa subito il cliente</p>
        </div>
      </div>

      <div className="form-grid">
        <div className="card">
          <div className="card-body">
            <div className="field">
              <span className="label">Email del cliente</span>
              <div style={{ position: 'relative' }}>
                <div className="search">
                  <Icon name="at" size={16} className="search-ico" />
                  <input className="input" type="email"
                    placeholder="cliente@esempio.it"
                    value={recipientEmail}
                    onChange={e => { setRecipientEmail(e.target.value); setShowEmailList(true) }}
                    onFocus={() => setShowEmailList(true)}
                    onBlur={() => setTimeout(() => setShowEmailList(false), 150)} />
                </div>
                {showEmailList && filteredEmails.length > 0 && (
                  <div className="pop">
                    {filteredEmails.map(email => (
                      <div key={email} className="pop-item" onMouseDown={() => { setRecipientEmail(email); setShowEmailList(false) }}>
                        <Avatar name={email} size={22} />
                        <span className="truncate">{email}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <span className="hint">Facoltativa. Clicca nel campo per scegliere tra le email già usate.</span>
            </div>

            <div className="field">
              <span className="label">Oggetto<span className="req">*</span></span>
              <input className="input" placeholder="Descrivi brevemente la richiesta" value={title} onChange={e => setTitle(e.target.value)} />
            </div>

            <div className="field">
              <span className="label">Riferimento interno</span>
              <input className="input" placeholder="Es. ordine 957, pratica GLS, reso Rossi…" maxLength={100}
                value={internalRef} onChange={e => setInternalRef(e.target.value)} />
              <span className="hint">Facoltativo. Lo vedete solo voi, non va al cliente: serve per ritrovare il ticket con la ricerca.</span>
            </div>

            <div className="field">
              <span className="label">Descrizione</span>
              <textarea className="textarea" rows={7}
                placeholder="Aggiungi i dettagli: cosa è successo, numero d'ordine, prodotto…"
                value={description} onChange={e => setDescription(e.target.value)} onPaste={handlePaste} />
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <span className="label">Allegati</span>
              <label className={`dropzone${drag ? ' drag' : ''}`}
                onDragOver={e => { e.preventDefault(); setDrag(true) }}
                onDragLeave={() => setDrag(false)}
                onDrop={onDrop}>
                <span className="dz-ico"><Icon name="upload" size={18} /></span>
                <span><strong>Clicca per caricare</strong> oppure trascina qui i file</span>
                <span className="hint">Foto, PDF, documenti fino a {MAX_FILE_MB} MB — puoi anche incollare uno screenshot nella descrizione</span>
                <input type="file" multiple hidden onChange={handleFileSelect} />
              </label>
              {pendingFiles.length > 0 && (
                <div className="composer-files" style={{ padding: '10px 0 0' }}>
                  {pendingFiles.map((f, i) => (
                    <span key={i} className="file-pill">
                      <FileIcon type={f.type} size={14} />
                      <span className="truncate">{f.name}</span>
                      <span className="faint">{formatSize(f.size)}</span>
                      <button type="button" onClick={() => removePendingFile(i)} title="Rimuovi"><Icon name="x" size={13} /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className={`alert ${created ? 'alert-warning' : 'alert-error'}`} style={{ marginTop: 16 }}>
                <Icon name="alert" size={16} /> <span>{error}</span>
              </div>
            )}
          </div>

          <div className="form-actions">
            {created ? (
              <button className="btn btn-primary" onClick={onCreated}><Icon name="arrowRight" size={15} /> Vai ai ticket</button>
            ) : (
              <>
                <button className="btn btn-secondary" onClick={onCreated}>Annulla</button>
                <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                  {loading ? <Spinner size={14} /> : <Icon name={hasEmail ? 'send' : 'check'} size={15} />}
                  {loading ? (pendingFiles.length ? 'Caricamento allegati…' : 'Creazione…') : hasEmail ? 'Crea e invia al cliente' : 'Crea ticket'}
                </button>
              </>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card panel">
            <h3 className="panel-title">Assegnazione</h3>
            <label className="field" style={{ marginBottom: 10 }}>
              <span className="label">Operatore</span>
              <select className="select" value={assignedTo} onChange={e => setAssignedTo(e.target.value)}>
                <option value="">— Non assegnato —</option>
                {profiles.map(p => <option key={p.id} value={p.id}>{p.name}{p.id === profile.id ? ' (tu)' : ''}</option>)}
              </select>
            </label>
            {assignedTo !== profile.id && (
              <button className="btn btn-soft btn-sm btn-block" onClick={() => setAssignedTo(profile.id)}>
                <Icon name="userPlus" size={15} /> Assegna a me
              </button>
            )}
          </div>

          <div className="card panel">
            <h3 className="panel-title">Come funziona</h3>
            <div className="tip">
              <span className="tip-ico"><Icon name="mail" size={14} /></span>
              <span>Se indichi l'email, il cliente riceve subito la conferma con il numero del ticket.</span>
            </div>
            <div className="tip">
              <span className="tip-ico"><Icon name="message" size={14} /></span>
              <span>Quando il cliente risponde a quella email, il messaggio si aggiunge da solo al ticket.</span>
            </div>
            <div className="tip">
              <span className="tip-ico"><Icon name="lock" size={14} /></span>
              <span>Senza email il ticket resta interno: utile per promemoria e lavorazioni.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
